import { cn } from '../../utils/cn';

export type SpinnerSize = 'sm' | 'md' | 'lg';

export interface SpinnerProps {
    className?: string;
    /** 14px / 20px / 32px. Omitted (the in-control default) leaves sizing entirely to `className`. */
    size?: SpinnerSize;
    /**
     * Decorative (default): renders `aria-hidden`, no `role` — for a spinner
     * nested in a control (Button, IconButton) that already carries its own
     * `aria-busy`. Pass `false` for a standalone spinner, which gets
     * `role="status"` and an accessible name from `aria-label`.
     */
    decorative?: boolean;
    /** Accessible name when `decorative` is false. Defaults to "Loading". */
    'aria-label'?: string;
}

const SIZE_CLASSES: Record<SpinnerSize, string> = {
    sm: 'h-3.5 w-3.5', // 14px
    md: 'h-5 w-5', // 20px
    lg: 'h-8 w-8', // 32px
};

const BASE_CLASSES = 'animate-spin motion-reduce:animate-none motion-reduce:opacity-60';

/**
 * Shared spinner. Extended by DS-08 (#178) with the standalone size scale
 * and `role="status"` on top of the minimal in-control spinner Button and
 * IconButton already relied on.
 */
export function Spinner({ className, size, decorative = true, 'aria-label': ariaLabel = 'Loading' }: SpinnerProps) {
    return (
        <svg
            className={cn(BASE_CLASSES, size && SIZE_CLASSES[size], className)}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            role={decorative ? undefined : 'status'}
            aria-hidden={decorative ? true : undefined}
            aria-label={decorative ? undefined : ariaLabel}
        >
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
            />
        </svg>
    );
}

export default Spinner;
